import {
  Breakpoints,
  DisableFocusWrapper,
  LinkButton,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';
import React from 'react';
import { EmissionsModel } from '../../typings/general';
import { AppConstants } from '../../utils/app-constants';
import { ParagraphSection } from '../paragraph-section';
import {
  ArrowContainer,
  BaseInformationContainer,
  CustomParagraphContainer,
  LinkTextContainer,
} from './styled';

import { getEnginesLinkInformation } from '../../utils';
import { useFeatureAppConfig } from '../../services/use-feature-app-config';

interface BaseInformationSectionProps {
  readonly dataLoaded?: boolean;
  readonly emissionsData?: EmissionsModel;
}

export const BaseInformationSection: React.FC<BaseInformationSectionProps> = (
  props,
) => {
  const { dataLoaded, emissionsData } = props;
  const { faHostServerUrl = '' } = useFeatureAppConfig();
  const enginesLinkInfo = getEnginesLinkInformation(
    emissionsData,
    faHostServerUrl,
  );

  return (
    <BaseInformationContainer>
      <TextCustom
        appearance={TextAppearance.headline100}
        style={{
          fontSize: TextSize.TextSize32,
          lineHeight: TextLineHeight.TextLineHeight36,
          fontWeight: TextWeight.bold,
        }}
      >
        Key Information
      </TextCustom>

      <ParagraphSection
        head={'Diesel Emissions Settlement Program'}
        richtext={
          'For additional information on the Diesel Emissions Settlement Program and to view a full list of included Volkswagen and Audi TDI models click <b>[here](https://www.vwcourtsettlement.com/en/)</b>.'
        }
      />
      <ParagraphSection
        head={'Emissions Modification Process'}
        richtext={`If you own or lease an affected vehicle, you have the option to keep your vehicle and have an Emissions Modification performed at no cost (if the modification is approved by the EPA & CARB). Learn more about the process <b>[here.](${AppConstants.EmissionsModificationOptionPDF})</b>`}
      />

      <ParagraphSection
        head={'Approved Emissions Modification and Warranty Disclosures'}
        richtext={
          'When an emission modification receives approval from the EPA and CARB, current owners and lessees of the affected vehicles will be notified and informed via first class mail of any associated impacts to the vehicle, including the warranty information associated with the emissions modification. Current and prospective owners and lessees can also view this notification through the links below.'
        }
      />

      {dataLoaded && emissionsData?.remedyAvailable && (
        <CustomParagraphContainer>
          <DisableFocusWrapper>
            <TextCustom
              appearance={TextAppearance.headline200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  lineHeight: TextLineHeight.TextLineHeight16,
                  fontWeight: TextWeight.bold,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize18,
                  lineHeight: TextLineHeight.TextLineHeight30,
                  fontWeight: TextWeight.bold,
                },
              }}
            >
              <LinkButton
                onClick={() => {
                  window.open(enginesLinkInfo.url, 'Download');
                }}
              >
                <LinkTextContainer>
                  Emissions Modification and Warranty Disclosure Letter for
                  vehicles with Generation {enginesLinkInfo.label} engines{' '}
                  <ArrowContainer>→</ArrowContainer>
                </LinkTextContainer>
              </LinkButton>
            </TextCustom>
          </DisableFocusWrapper>
        </CustomParagraphContainer>
      )}

      <ParagraphSection
        textColor="#515a5e"
        richtext={`For a detailed list of affected vehicles and generation of engines, click <b>[here](${
          faHostServerUrl + AppConstants.AffectedVehiclesPDF
        })</b>.`}
      />

      <ParagraphSection
        textColor="#515a5e"
        selfTarget={true}
        richtext={
          'For any additional questions regarding recalls/service campaigns, please contact <b>[VW Customer Support.](/en/contact.html)</b>'
        }
      />
    </BaseInformationContainer>
  );
};
